import * as React from "react"

export interface ConfirmationDialogProps {
  id: string
  title: string
  description: string | React.ReactNode
  confirmText?: string
  cancelText?: string
  alternativeText?: string
  variant?: "default" | "destructive"
  onConfirm: () => void
  onCancel?: () => void
  onAlternative?: () => void
}

const CONFIRMATION_LIMIT = 1

type ConfirmationDialog = ConfirmationDialogProps & {
  open: boolean
}

const actionTypes = {
  ADD_CONFIRMATION: "ADD_CONFIRMATION",
  UPDATE_CONFIRMATION: "UPDATE_CONFIRMATION",
  DISMISS_CONFIRMATION: "DISMISS_CONFIRMATION",
  REMOVE_CONFIRMATION: "REMOVE_CONFIRMATION",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_CONFIRMATION"]
      confirmation: ConfirmationDialog
    }
  | {
      type: ActionType["UPDATE_CONFIRMATION"]
      confirmation: Partial<ConfirmationDialog>
    }
  | {
      type: ActionType["DISMISS_CONFIRMATION"]
      confirmationId?: ConfirmationDialog["id"]
    }
  | {
      type: ActionType["REMOVE_CONFIRMATION"]
      confirmationId?: ConfirmationDialog["id"]
    }

interface State {
  confirmations: ConfirmationDialog[]
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_CONFIRMATION":
      return {
        ...state,
        confirmations: [action.confirmation, ...state.confirmations].slice(0, CONFIRMATION_LIMIT),
      }

    case "UPDATE_CONFIRMATION":
      return {
        ...state,
        confirmations: state.confirmations.map((c) =>
          c.id === action.confirmation.id ? { ...c, ...action.confirmation } : c
        ),
      }

    case "DISMISS_CONFIRMATION": {
      const { confirmationId } = action

      return {
        ...state,
        confirmations: state.confirmations.map((c) =>
          c.id === confirmationId || confirmationId === undefined
            ? {
                ...c,
                open: false,
              }
            : c
        ),
      }
    }
    case "REMOVE_CONFIRMATION":
      if (action.confirmationId === undefined) {
        return {
          ...state,
          confirmations: [],
        }
      }
      return {
        ...state,
        confirmations: state.confirmations.filter((c) => c.id !== action.confirmationId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { confirmations: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Confirmation = Omit<ConfirmationDialog, "id" | "open">

function confirmation({ ...props }: Confirmation) {
  const id = genId()

  const update = (props: ConfirmationDialog) =>
    dispatch({
      type: "UPDATE_CONFIRMATION",
      confirmation: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_CONFIRMATION", confirmationId: id })

  dispatch({
    type: "ADD_CONFIRMATION",
    confirmation: {
      ...props,
      id,
      open: true,
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

function useConfirmation() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    confirmation,
    dismiss: (confirmationId?: string) => dispatch({ type: "DISMISS_CONFIRMATION", confirmationId }),
  }
}

export { useConfirmation, confirmation } 